import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Row, Col, Button, message, DatePicker, TimePicker } from 'antd';
import DeliveryNoteTable from './DeliveryNoteTable';
import { CloseOutlined, ExclamationCircleOutlined, CheckCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import './AddToDeliveryNotePopup.less';
import axios from 'axios';
import { baseURL } from '../../../../globalUtils/axiosConfig';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import pdfIcon from '../../../../assets/c9785d49e2c04999be9f5d27fad98100.png';

const AddToDeliveryNotePopup = ({ visible, onClose, project_id, selectedItems = {} }) => {
  const { user } = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  
  const getCurrentUserName = () => {
    if (!user) return 'Current User';
    
    if (user.first_name && user.surname) {
      return `${user.first_name} ${user.surname}`;
    }
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.name) {
      return user.name;
    }
    if (user.username) {
      return user.username;
    }
    if (user.email) {
      return user.email;
    }
    
    return 'Current User';
  };
  
  const [formData, setFormData] = useState({
    to: '',
    attention: '',
    regarding: '',
    truckTrailerReg: '',
    driverName: '',
    driverArrivedAt: null,
    driverArrivedTime: null,
    receiverName: '',
    receivedAt: null,
    receivedAtTime: null,
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemsData, setSelectedItemsData] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deliveryNoteResponse, setDeliveryNoteResponse] = useState(null);
  const confirmationRef = useRef(null);
  
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Format numbers with comma as decimal separator and limit to one decimal place
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0,0';
    
    // Convert to number to handle any string inputs
    const num = Number(value);
    
    // Check if it's a whole number
    if (Number.isInteger(num)) {
      // For whole numbers, add ',0' at the end
      return `${num},0`;
    } else {
      // For decimal numbers, keep only one decimal place and replace '.' with ','
      const formatted = num.toFixed(1);
      return formatted.replace('.', ',');
    }
  };
  
  // Format percentage values with comma as decimal separator
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0,00%';
    
    // Convert to number to handle any string inputs
    const num = Number(value);
    
    // For whole numbers with 3 digits or less (like 100), add ',00'
    if (Number.isInteger(num) && num.toString().length <= 3) {
      return `${num},00%`;
    } else {
      // For decimal numbers, keep two decimal places and replace '.' with ','
      const formatted = num.toFixed(2);
      return `${formatted.replace('.', ',')}%`;
    }
  };

  // Process the selected items when they change
  useEffect(() => {
    // Check if we have any selected items
    const materialTypes = Object.keys(selectedItems);
    if (materialTypes.length === 0) {
      // If no items are selected, use sample data for testing
      setTableData();
      return;
    }

    setLoading(true);
    try {
      // Extract all items from all material types
      const allItemsData = [];
      
      materialTypes.forEach(materialType => {
        if (selectedItems[materialType] && selectedItems[materialType].items) {
          // Get all items for this material type
          const itemsInType = Object.values(selectedItems[materialType].items);
          
          // Add material type info to each item
          const itemsWithType = itemsInType.map(item => ({
            ...item,
            materialType,
            uom: selectedItems[materialType].uom || ''
          }));
          
          allItemsData.push(...itemsWithType);
        }
      });
      
      // Store the selected items data
      setSelectedItemsData(allItemsData);
      
      // Transform the data into the format expected by DeliveryNoteTable
      transformDataForTable(allItemsData);
    } catch (error) {
      console.error('Error processing selected items data:', error);
      message.error('Failed to process selected items data');
    } finally {
      setLoading(false);
    }
  }, [selectedItems]);
  
  // Transform the data into the format expected by DeliveryNoteTable
  const transformDataForTable = (itemsData) => {
    if (!itemsData || itemsData.length === 0) {
      setTableData(selectedItems);
      return;
    }
    
    // Group items by category
    const groupedByCategory = {};
    
    itemsData.forEach(item => {
      const category = item.type?.toLowerCase() === 'bolts' ? 'BOLTS' : 'MATERIAL';
      const uom = item.type?.toLowerCase() === 'bolts' ? 'pcs' : 'kg';
      
      if (!groupedByCategory[category]) {
        groupedByCategory[category] = {
          items: [],
          totalQuantity: 0,
          totalGross: 0,
          uom
        };
      }
      
      // Use the quantity and total from the item data
      const quantity = item.qty || 0;
      const totalGross = item.totalGross || 0;
      
      // Add to category totals
      groupedByCategory[category].totalQuantity += quantity;
      groupedByCategory[category].totalGross += totalGross;
      
      // Add item to the category
      groupedByCategory[category].items.push({
        id: item.id,
        item: groupedByCategory[category].items.length + 1, // Sequential item number within category
        model_no: item.model || '',
        drawing_mark_no: item.markNo || '',
        description: item.description || '',
        quantity,
        total_gross: totalGross,
        uom,
        category
      });
    });
    
    // Convert the grouped data into the format expected by DeliveryNoteTable
    const tableItems = [];
    let itemId = 1;
    
    Object.keys(groupedByCategory).forEach(category => {
      const categoryData = groupedByCategory[category];
      const uom = categoryData.uom;
      
      // Add category header
      tableItems.push({
        id: itemId++,
        category: `${category} (${uom})`,
        isCategory: true
      });
      
      // Add category items
      categoryData.items.forEach(item => {
        tableItems.push({
          ...item,
          id: itemId++
        });
      });
      
      // Add category total
      tableItems.push({
        id: itemId++,
        category,
        isTotal: true,
        quantity: categoryData.totalQuantity,
        total_gross: categoryData.totalGross,
        uom
      });
    });
    
    setTableData(tableItems);
  };

  const handleGenerateClick = () => {
    // Validate required fields
    if (!formData.to || !formData.attention || !formData.regarding || !formData.truckTrailerReg) {
      message.error('Please fill in all required fields');
      return;
    }
    
    // Show inline confirmation
    setShowConfirmation(true);
  };

  // Auto-scroll to confirmation alert when it appears
  useEffect(() => {
    if (showConfirmation && confirmationRef.current) {
      // Use setTimeout to ensure the DOM has been updated
      setTimeout(() => {
        confirmationRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [showConfirmation]);

  const handleConfirmGenerate = async () => {
    setLoading(true);
    setShowConfirmation(false); // Hide confirmation immediately for better UX
    
    try {
      // Prepare items array for the API
      const items = [];
      let progressStepType = null;
      
      // Extract all items from the grouped structure
      Object.keys(selectedItems).forEach(materialType => {
        if (selectedItems[materialType] && selectedItems[materialType].items) {
          Object.values(selectedItems[materialType].items).forEach(item => {
            // Store progressStepType from the first item found
            if (progressStepType === null && item.progressStepType) {
              progressStepType = item.progressStepType;
            }
            
            items.push({
              materialControlRegisterId: item.id,
              quantity: item.qty || 0
            });
          });
        }
      });

      if (progressStepType === 'qcRelease') {
        progressStepType = 'qc_release'; 
      }else if (progressStepType === 'deliveryNoteCorrisonProtection') {
        progressStepType = 'corrosion_protection'; 
      }else if (progressStepType === 'deliveryToSite') {
        progressStepType = 'delivery_to_site'; 
      }
        
      // Prepare data for API submission according to the required format
      const deliveryNoteData = {
        // delNoteNo: "A000123-0006", // This should be generated by backend or calculated
        projectId: project_id,
        to: formData.to,
        attention: formData.attention,
        re: formData.regarding,
        date: new Date().toISOString(),
        truckTrailerReg: formData.truckTrailerReg,
        notes: "", // Optional notes field - can be added to form if needed
        driverName: formData.driverName || "",
        dispatcherName: getCurrentUserName(),
        dispatchedAt: new Date().toISOString(),
        progressStepType: progressStepType,
        items: items,
        driverArrivedAt: formData.driverArrivedAt ? formData.driverArrivedAt.toISOString() : null,
        driverArrivedTime: formData.driverArrivedTime ? formData.driverArrivedTime.format('HH:mm') : null,
        receivedAt: formData.receivedAt ? formData.receivedAt.toISOString() : null,
        receivedTime: formData.receivedAtTime ? formData.receivedAtTime.format('HH:mm') : null,
        receiverName: formData.receiverName || ""
      };
                  
      // Make the POST API call
      const response = await axios.post(
        `${baseURL}/delivery-notes`,
        deliveryNoteData,
      );
      
      if (response.status === 200 || response.status === 201) {
        message.success('Delivery note generated successfully');
        setDeliveryNoteResponse(response.data);
        setShowSuccess(true);
        // Redirect to delivery notes page for this project
        // navigate(`/project/delivery-notes/${project_id}`);
      } else {
        throw new Error('Failed to generate delivery note');
      }
      
    } catch (error) {
      console.error('Error generating delivery note:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 'Failed to generate delivery note';
        message.error(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleClose = () => {
    if (showSuccess) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const handleOpenPDF = () => {
    if (deliveryNoteResponse && deliveryNoteResponse.attachments && deliveryNoteResponse.attachments.length > 0) {
      const pdfAttachment = deliveryNoteResponse.attachments.find(attachment => 
        attachment.mimetype === 'application/pdf'
      );
      
      if (pdfAttachment) {
        // Open PDF in new tab using the file path from the API response
        const pdfUrl = `${baseURL}/${pdfAttachment.path}`;
        window.open(pdfUrl, '_blank');
      }
    }
  };


  return (
    <Modal
      title={
        <div className="delivery-note-popup-header">
          <span className="delivery-note-title">DELIVERY NOTE</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1123}
      className="delivery-note-popup"
      maskClosable={false}
      centered
      closeIcon={<CloseOutlined className="custom-close-icon" />}
    >
      {showSuccess ? (
        <div className="delivery-note-content">
        <div className="success-content">
          <p className="success-message" style={{ marginLeft: '30px', fontSize: '12px' }}>The Delivery Note has been generated successfully.</p>
          
          <div className="pdf-download-section">
            <img src={pdfIcon} alt="PDF" className="pdf-icon" style={{ marginLeft: '30px', width: '26px', height: '32px' }} />
            <span 
              className="pdf-filename" 
              style={{ 
                marginLeft: '10px', 
                fontSize: '12px',
                color: '#030508',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={handleOpenPDF}
            >
              {deliveryNoteResponse ? 
                `${new Date(deliveryNoteResponse.date).toISOString().split('T')[0]} - ${deliveryNoteResponse.delNoteNo}.pdf` : 
                'delivery-note.pdf'
              }
            </span>
          </div>
          
          <div className="horizontal-divider"></div>
          
          <div className="success-actions" style={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              onClick={() => navigate(`/project/delivery-notes/${project_id}`)}
              className="delivery-register-button"
              style={{
                border: '1px solid #f25003',
                color: '#f25003',
                backgroundColor: 'white',
                marginBottom: '30px'
              }}
            >
              GO TO DELIVERY NOTE REGISTER
            </Button>
          </div>
        </div>
        </div>
      ) : (
        <div className="delivery-note-content">
        <div className="form-section">
        <Row gutter={[24, 16]} className="form-row">
          <Col span={12}>
            <div className="form-group inline left-aligned">
              <label className="form-label">TO: <span className="required">*</span></label>
              <Input 
                placeholder="Company Name" 
                value={formData.to}
                onChange={(e) => handleInputChange('to', e.target.value)}
                className="form-input"
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group inline">
              <label className="form-label truck-label">DEL. NOTE NO:</label>
              <div className="static-value">XXXXXXX-XXXX</div>
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 16]} className="form-row">
          <Col span={12}>
            <div className="form-group inline left-aligned">
              <label className="form-label">ATTENTION: <span className="required">*</span></label>
              <Input 
                placeholder="Contact Name" 
                value={formData.attention}
                onChange={(e) => handleInputChange('attention', e.target.value)}
                className="form-input"
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group inline">
              <label className="form-label truck-label">TRUCK & TRAILER REG: <span className="required">*</span></label>
              <Input 
                placeholder="Truck & Trailer Registrations" 
                value={formData.truckTrailerReg}
                onChange={(e) => handleInputChange('truckTrailerReg', e.target.value)}
                className="form-input-truck-trailer"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 16]} className="form-row">
          <Col span={12}>
            <div className="form-group inline left-aligned">
              <label className="form-label">RE: <span className="required">*</span></label>
              <Input 
                placeholder="Regarding" 
                value={formData.regarding}
                onChange={(e) => handleInputChange('regarding', e.target.value)}
                className="form-input"
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group inline">
              <label className="form-label truck-label">DATE:</label>
              <div className="static-value">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
            </div>
          </Col>
        </Row>
      </div>
        <div className="horizontal-divider"></div>
        <DeliveryNoteTable progressData={selectedItems} loading={loading} />
        <div className="horizontal-divider-below"></div>

        {/* Signature Boxes */}
        <div className="signature-boxes-container">
          <div className="signature-box">
            <div className="signature-box-header">DISPATCHER</div>
            <div className="signature-field">
              <label>NAME:</label>
              <span>{getCurrentUserName()}</span>
            </div>
            <div className="signature-field">
              <label>DATE:</label>
              <span>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
            <div className="signature-field">
              <label>TIME DISPATCHED:</label>
              <span>{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
            </div>
          </div>

          <div className="signature-box">
            <div className="signature-box-header">DRIVER</div>
            <div className="signature-field">
              <label>NAME: *</label>
              <Input 
                placeholder="Name" 
                className="signature-input" 
                value={formData.driverName}
                onChange={(e) => handleInputChange('driverName', e.target.value)}
              />
            </div>
            <div className="signature-field">
              <label>DATE:</label>
              <DatePicker 
                placeholder="Select Date" 
                className="signature-date-picker"
                value={formData.driverArrivedAt}
                onChange={(date) => handleInputChange('driverArrivedAt', date)}
                format="DD MMM YY"
              />
            </div>
            <div className="signature-field">
              <label>TIME ARRIVED:</label>
              <TimePicker 
                placeholder="Select Time" 
                className="signature-input"
                format="HH:mm"
                disabled
              />
            </div>
          </div>

          <div className="signature-box">
            <div className="signature-box-header">RECEIVER / CLIENT</div>
            <div className="signature-field">
              <label>NAME:</label>
              <Input 
                placeholder="Name" 
                className="signature-input" 
                value={formData.receiverName}
                onChange={(e) => handleInputChange('receiverName', e.target.value)}
              />
            </div>
            <div className="signature-field">
              <label>DATE:</label>
              <DatePicker 
                placeholder="Select Date" 
                className="signature-date-picker"
                value={formData.receivedAt}
                onChange={(date) => handleInputChange('receivedAt', date)}
                format="DD MMM YY"
              />            
              </div>
            <div className="signature-field">
              <label>TIME RECEIVED:</label>
              <TimePicker 
                placeholder="Select Time" 
                className="signature-input"
                format="HH:mm"
                disabled
              />
            </div>
          </div>
        </div>

        <div className="footer-actions">
          <Button 
            type="primary" 
            onClick={handleGenerateClick}
            className="submit-button"
            disabled={showConfirmation}
          >
            Generate Delivery Note
          </Button>
          
          {showConfirmation && (
            <div className="inline-confirmation" ref={confirmationRef}>
              <div className="confirmation-message">
                <ExclamationCircleOutlined className="confirmation-icon" />
                <div className="confirmation-text">
                  <p>Are you sure you want to generate this Delivery Note?</p>
                  <p className="confirmation-subtext">Please note this action cannot be undone.</p>
                </div>
              </div>
              <div className="confirmation-actions">
                <Button 
                  type="primary" 
                  onClick={handleConfirmGenerate}
                  loading={loading}
                  className="confirm-yes-button"
                >
                  Yes
                </Button>
                <Button 
                  onClick={handleCancelConfirmation}
                  className="confirm-no-button"
                >
                  No
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

    </Modal>
  );
};

export default AddToDeliveryNotePopup;
